// src/features/settings/ConnectedAccountsSettings.tsx
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Divider,
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, CircularProgress
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { useTranslation, Trans } from 'react-i18next';
import * as authService from '../../api/authService';
import { fetchUserInfo } from '../auth/authSlice';
import PasswordField from '../../components/common/PasswordField';
import { validatePassword } from '../../utils/validation'; // UPDATED: Added import for password validation

const ConnectedAccountsSettings: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.auth);
    const { t } = useTranslation();
    const [isDisconnecting, setDisconnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
    const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
    const [newPasswordTouched, setNewPasswordTouched] = useState(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

    // This check assumes user might have been created via Google without a password.
    // The API will tell us if a password is required.
    const requiresPassword = user?.email.endsWith('@gmail.com');

    // Validate passwords only if fields have been touched
    useEffect(() => {
        // Validate new password
        if (newPasswordTouched) {
            const passwordError = validatePassword(newPassword);
            if (!newPassword.trim()) {
                setNewPasswordError(t('new_password_required'));
            } else if (passwordError) {
                setNewPasswordError(passwordError);
            } else {
                setNewPasswordError(null);
            }
        }

        // Validate confirm password
        if (confirmPasswordTouched) {
            if (!confirmPassword.trim()) {
                setConfirmPasswordError(t('confirm_password_required'));
            } else if (newPassword !== confirmPassword) {
                setConfirmPasswordError(t('passwords_do_not_match'));
            } else {
                setConfirmPasswordError(null);
            }
        }
    }, [newPassword, confirmPassword, newPasswordTouched, confirmPasswordTouched, t]);

    const handleOpenDialog = () => {
        setOpenDialog(true);
        setError(null);
        setNewPassword('');
        setConfirmPassword('');
        setNewPasswordTouched(false);
        setConfirmPasswordTouched(false);
        setNewPasswordError(null);
        setConfirmPasswordError(null);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleDisconnect = async () => {
        // Mark fields as touched to trigger validation
        setNewPasswordTouched(true);
        setConfirmPasswordTouched(true);

        if (requiresPassword) {
            const passwordError = validatePassword(newPassword);
            if (!newPassword.trim()) {
                setNewPasswordError(t('new_password_required'));
                return;
            } else if (passwordError) {
                setNewPasswordError(passwordError);
                return;
            }
            if (!confirmPassword.trim()) {
                setConfirmPasswordError(t('confirm_password_required'));
                return;
            } else if (newPassword !== confirmPassword) {
                setConfirmPasswordError(t('passwords_do_not_match'));
                return;
            }
        }

        setDisconnecting(true);
        setError(null);

        try {
            await authService.disconnectGoogle(requiresPassword ? { newPassword } : undefined);
            // Success, refetch user to update `isGoogleLinked` status
            await dispatch(fetchUserInfo());
            handleCloseDialog();
        } catch (err: any) {
            setError(t('error_disconnect_google'));
        } finally {
            setDisconnecting(false);
        }
    };

    // Determine if the Disconnect button should be disabled
    const isButtonDisabled = isDisconnecting ||
        (requiresPassword && (
            (newPasswordTouched && !!newPasswordError) ||
            (confirmPasswordTouched && !!confirmPasswordError) ||
            !newPassword.trim() ||
            !confirmPassword.trim()
        ));

    if (!user) {
        return <CircularProgress />;
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>{t('menu_connections')}</Typography>

            <Box sx={{ py: 3 }}>
                {user.isGoogleLinked && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('connected_accounts_desc')}
                    </Typography>
                )}
                {user.isGoogleLinked ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <GoogleIcon color="error" />
                                <Typography>{t('connected_with_google', { email: user.email })}</Typography>
                            </Box>
                            <Button variant="outlined" color="error" onClick={handleOpenDialog}>{t('disconnect')}</Button>
                        </Box>
                    </Paper>
                ) : (
                    <Paper variant="outlined" sx={{ p: 2, background: theme => theme.palette.background.default }}>
                        <Typography variant="h6" gutterBottom>{t('link_google_account')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('link_google_account_desc')}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" component="div">
                            {!user.email.endsWith('@gmail.com') ? (
                                <>
                                    1. {t('link_google_step1')}<strong>{t('profile_settings')}</strong>.<br/>
                                    2. {t('link_google_step2')}
                                </>
                            ) : (
                                <Trans i18nKey="link_google_instruction" values={{ email: user.email }} components={{ strong: <strong /> }} />
                            )}
                        </Typography>
                    </Paper>
                )}
            </Box>

            {/* Disconnect Confirmation Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{t('disconnect_google_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('disconnect_google_desc')}
                    </DialogContentText>
                    {requiresPassword && (
                        <Box sx={{ mt: 2 }}>
                            <DialogContentText color="text.primary" sx={{ mb: 1 }}>
                                {t('create_password_prompt')}
                            </DialogContentText>
                            <PasswordField
                                label={t('new_password')}
                                value={newPassword}
                                onChange={e => {
                                    setNewPassword(e.target.value);
                                    setNewPasswordTouched(true);
                                }}
                                error={newPasswordTouched && !!newPasswordError}
                                helperText={newPasswordTouched ? newPasswordError : null}
                            />
                            <PasswordField
                                label={t('confirm_new_password')}
                                value={confirmPassword}
                                onChange={e => {
                                    setConfirmPassword(e.target.value);
                                    setConfirmPasswordTouched(true);
                                }}
                                error={confirmPasswordTouched && !!confirmPasswordError}
                                helperText={confirmPasswordTouched ? confirmPasswordError : null}
                            />
                        </Box>
                    )}
                    {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={isDisconnecting}>{t('cancel')}</Button>
                    <Button
                        onClick={handleDisconnect}
                        color="error"
                        disabled={isButtonDisabled}
                    >
                        {isDisconnecting ? <CircularProgress size={24} /> : t('disconnect')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ConnectedAccountsSettings;